import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Award, Download, Sparkles, Trophy } from "lucide-react";

export default function Certificates() {
  const { user } = useAuth();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/certificates").then(({ data }) => {
      setCerts(data); setLoading(false);
    });
  }, []);

  const view = (c) => {
    // Build certificate HTML and render via Blob URL (avoids document.write XSS vector)
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Certificate - ${esc(c.student_name)}</title><style>
      body{font-family:'Outfit',Georgia,serif;background:#F8FAFC;padding:50px;margin:0}
      .cert{background:white;border:10px double #0F2E4A;padding:80px 60px;max-width:900px;margin:auto;text-align:center;position:relative;box-shadow:0 25px 50px rgba(15,46,74,0.12)}
      .ribbon{position:absolute;top:30px;right:30px;width:60px;height:60px;border-radius:50%;background:#D97706;color:white;display:grid;place-items:center;font-weight:700;font-size:22px;font-family:Georgia,serif}
      .sub{color:#475569;letter-spacing:8px;text-transform:uppercase;font-size:11px;font-weight:600}
      h1{font-size:52px;color:#0F2E4A;letter-spacing:4px;margin:14px 0 6px;font-family:Georgia,serif;font-weight:700}
      .institute{color:#059669;letter-spacing:6px;text-transform:uppercase;font-size:12px;font-weight:600;margin-top:4px}
      .line{color:#475569;line-height:1.7;margin:8px 0;font-size:15px}
      .name{font-size:42px;margin:30px 0 8px;color:#0F172A;font-family:Georgia,serif;font-style:italic;border-bottom:3px double #059669;display:inline-block;padding:0 30px 12px}
      .program{font-size:22px;color:#0F172A;font-weight:600;margin:14px 0 6px}
      .stats{display:flex;justify-content:center;gap:50px;margin:30px 0;flex-wrap:wrap}
      .stat{text-align:center}
      .stat .v{font-size:34px;color:#059669;font-weight:700;font-family:Georgia,serif}
      .stat .l{font-size:10px;color:#64748B;letter-spacing:3px;text-transform:uppercase;margin-top:4px}
      .footer{margin-top:50px;display:flex;justify-content:space-between;align-items:end;font-size:11px;color:#64748B}
      .sig{text-align:center}
      .sig .name-sig{border-top:1px solid #94A3B8;padding-top:6px;font-weight:600;color:#0F172A;font-style:italic}
      .id{font-family:'IBM Plex Mono',monospace;font-size:10px;color:#94A3B8;letter-spacing:1px}
      @media print{body{padding:0;background:white}.cert{box-shadow:none}}
    </style></head><body>
    <div class="cert">
      <div class="ribbon">★</div>
      <div class="sub">Certificate of Achievement</div>
      <h1>EXCELLENCE</h1>
      <div class="institute">Quiddity Institute of Excellence</div>
      <div class="line" style="margin-top:36px">This is to certify that</div>
      <div class="name">${esc(c.student_name)}</div>
      <div class="line">${c.student_id ? `Student ID: ${esc(c.student_id)}` : ""}</div>
      <div class="line" style="margin-top:18px">has demonstrated outstanding commitment in the</div>
      <div class="program">${esc(c.course || "Medical Coding")} Program</div>
      <div class="line">by maintaining excellent attendance throughout the cohort</div>
      <div class="stats">
        <div class="stat"><div class="v">${esc(c.score)}%</div><div class="l">Attendance</div></div>
        <div class="stat"><div class="v">${esc(c.batch || "—")}</div><div class="l">Batch</div></div>
        <div class="stat"><div class="v">${esc(c.trainer || "—")}</div><div class="l">Trainer</div></div>
      </div>
      <div class="footer">
        <div class="id">CERT-ID: ${esc((c.id || "").toUpperCase())}<br/>ISSUED: ${esc(new Date(c.issued_on).toLocaleDateString())}</div>
        <div class="sig"><div style="height:50px"></div><div class="name-sig">Administrator &nbsp; · &nbsp; Quiddity</div></div>
      </div>
    </div>
    <div style="text-align:center;padding:30px 0"><button onclick="window.print()" style="background:#0F2E4A;color:white;border:none;padding:12px 28px;border-radius:6px;font-size:13px;cursor:pointer;font-family:inherit;letter-spacing:1px">Print Certificate</button></div>
    </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) {
      URL.revokeObjectURL(url);
      return;
    }
    // Revoke the URL after the new tab has loaded; fallback after 60s
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div className="space-y-6" data-testid="certificates-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Achievements</div>
        <h1 className="font-heading text-3xl font-bold text-[#0F172A]">My Certificates</h1>
        <p className="text-sm text-[#475569] mt-1">Certificates are auto-issued when you maintain 85%+ attendance.</p>
      </div>

      {loading ? (
        <div className="text-[#475569]">Loading...</div>
      ) : certs.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E2E8F0] rounded-lg p-16 text-center">
          <Sparkles className="w-12 h-12 mx-auto text-[#D97706] mb-4" strokeWidth={1.5} />
          <h3 className="font-heading text-xl font-semibold text-[#0F172A]">Not eligible yet</h3>
          <p className="text-sm text-[#475569] mt-2 max-w-md mx-auto">
            Keep attending regularly! Once your attendance reaches <span className="font-semibold text-[#059669]">85%</span>, your {user.course || "Medical Coding"} Excellence certificate will be auto-issued here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger">
          {certs.map((c) => (
            <div key={c.id} className="relative border-2 border-[#D97706]/30 rounded-lg p-6 bg-gradient-to-br from-white via-[#FEF3C7]/20 to-white overflow-hidden">
              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-[#D97706]/10" />
              <div className="absolute -left-8 -bottom-8 w-32 h-32 rounded-full bg-[#059669]/10" />
              <div className="relative">
                <div className="flex items-start justify-between">
                  <Award className="w-10 h-10 text-[#D97706]" strokeWidth={1.5} />
                  <Trophy className="w-5 h-5 text-[#059669]" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D97706] font-bold mt-4">Certificate of Excellence</div>
                <h3 className="font-heading text-2xl font-bold text-[#0F172A] mt-1">{c.title}</h3>
                <div className="text-sm text-[#475569] mt-1">{c.batch} · {c.trainer}</div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Attendance</div>
                    <div className="text-2xl font-heading font-bold text-[#059669]">{c.score}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Issued</div>
                    <div className="text-sm font-medium text-[#0F172A] mt-1">{new Date(c.issued_on).toLocaleDateString()}</div>
                  </div>
                </div>
                <Button
                  onClick={() => view(c)}
                  data-testid={`view-cert-${c.id}`}
                  className="mt-5 w-full bg-[#0F2E4A] hover:bg-[#174169] text-white"
                >
                  <Download className="w-4 h-4 mr-1.5" /> View / Print Certificate
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
